import _ from 'lodash';
import {
    BudgetPhases,
    BudgetSections,
    BudgetExpenseOrIncome,
    BudgetBys,
    DEFAULT_BUDGET_BY,
    ORDERED_BUDGET_PHASES,
} from 'business/constants';
import {
    SECTION_FIELD,
    BUDGET_NAME_FIELD,
    BUDGET_LINE_ID,
    BUDGET_YEAR_FIELD,
    EXPENSE_INCOME_FIELD,
    INSCRIPTION_EXERCICE,
    INSCRIPTION_EXPENSE_INCOME,
    INSCRIPTION_INVESTMENT_OPERATING,
    LINE_CODE_FIELD,
    GESTIONNAIRE_SOFTWARE_ID,
    GESTIONNAIRE_TABLE,
    SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
} from 'routes/BudgetModule/config/constants';
import {
    BudgetProposalT,
    CadrageConfigT,
    QuerySettingsT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import {
    getRawBudgetVentilatedQuery,
    BudgetProposalVentilationBy,
} from 'routes/BudgetModule/config/queries';
import {
    hasFilterScope,
    getBlockValues,
} from 'components/SentenceFilter/helpers';

import {
    FilterOperations as FO,
    UserScopesT,
    ScopeT,
} from 'business/definitions';
import {
    getPhasedFieldsUntilPhase,
    getStatusFieldForPhase,
    isActiveBudgetPhase,
    isSimulatedPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { getRejectedStatusesForPhase } from './statusHelpers';
import { snakify } from 'helpers/syntaxHelper';

const getDimField = (dim: string, index: number): QueryDimensionT => ({
    field: dim,
    displayIndex: index,
});

const getInFiltersForValues = (
    field: string,
    values: string[],
): QueryFilterT[] => [
    {
        field,
        operation: FO.in_,
        value: values.join('|'),
    },
];

/**
 * Generates query filters relatives to budget proposals.
 * If a `budgetSection` is provided, it adds an additional filter for the specified budget section.
 */
export const getFiltersFromBudgetProposals = (
    budgetProposals: BudgetProposalT[],
    budgetSection?: BudgetSections | null,
): QueryFilterT[] => {
    const filters = getInFiltersForValues(
        BUDGET_NAME_FIELD,
        budgetProposals.map((bp) => bp.budgetName),
    );

    if (budgetSection) {
        filters.push({
            field: SECTION_FIELD,
            operation: FO.in_,
            value: budgetSection,
        });
    }
    return filters;
};

const getInscriptionFiltersFromBudgetProposals = (
    budgetProposals: BudgetProposalT[],
    budgetSection?: BudgetSections | null,
): QueryFilterT[] => {
    const filters = getInFiltersForValues(
        BUDGET_NAME_FIELD,
        budgetProposals.map((bp) => bp.budgetName),
    );

    if (budgetSection) {
        filters.push({
            field: INSCRIPTION_INVESTMENT_OPERATING,
            operation: FO.in_,
            value: budgetSection,
        });
    }
    return filters;
};

/**
 * Returns the filter that is used to get the value of cadrage.
 */
const getCadrageFilter = (
    budgetExercice: number,
    cadrageConfig: CadrageConfigT,
): QueryFilterT[] => {
    const filterValue = budgetExercice - cadrageConfig.yearOffset;

    return [
        {
            field: BUDGET_YEAR_FIELD,
            value: filterValue.toString(),
            operation: FO.equal,
        },
    ];
};

export const getExactBudgetYearFilter = (exercice: number): QueryFilterT[] => [
    {
        field: BUDGET_YEAR_FIELD,
        value: exercice.toString(),
        operation: FO.equal,
    },
];

const getBetweenBudgetYearFilter = (
    exercice: number,
    exerciceRange: number,
): QueryFilterT[] => [
    {
        field: BUDGET_YEAR_FIELD,
        value: (exercice - exerciceRange).toString(),
        operation: FO.greater_or_equal,
    },
    {
        field: BUDGET_YEAR_FIELD,
        value: exercice.toString(),
        operation: FO.lower_or_equal,
    },
];

export const getBudgetLineCodeFilter = (
    budgetLineCode: string,
): QueryFilterT => ({
    field: LINE_CODE_FIELD,
    value: budgetLineCode,
    operation: FO.equal,
});

/**
 * Returns the filter on the exercice.
 *
 * When isCadrage=True, the request is to get the amount realized N-years before
 * the budget proposal exercice.  NB: N is variable for each client, see getCadrageFilter.
 * Thus the filter is usually field budget.year = exercice -2
 *
 * When hasExerciceRange= True, we filter is exercice -2 < budget.year < exercice
 * Otherwise, we filter for budget.year = exercice
 */
export function getExerciceFilter(
    exercice: number,
    hasExerciceRange: boolean | undefined = true,
    isCadrage: boolean,
    exerciceRange: number = 2,
    cadrageConfig?: CadrageConfigT,
): QueryFilterT[] {
    if (isCadrage && cadrageConfig)
        return getCadrageFilter(exercice, cadrageConfig);
    if (!hasExerciceRange) return getExactBudgetYearFilter(exercice);
    return getBetweenBudgetYearFilter(exercice, exerciceRange);
}

function getInscriptionExerciceFilter(exercice: number): QueryFilterT[] {
    return [
        {
            field: INSCRIPTION_EXERCICE,
            value: exercice.toString(),
            operation: FO.equal,
        },
    ];
}

/**
 * Generates a query filter for the given expense or income type.
 */
export const getExpenseOrIncomeFilter = (
    expenseOrIncome: BudgetExpenseOrIncome,
): QueryFilterT[] => [
    {
        field: EXPENSE_INCOME_FIELD,
        operation: FO.in_,
        value: expenseOrIncome.toUpperCase(),
    },
];

export const getInscriptionExpenseOrIncomeFilter = (
    expenseOrIncome: BudgetExpenseOrIncome,
): QueryFilterT[] => [
    {
        field: INSCRIPTION_EXPENSE_INCOME,
        operation: FO.in_,
        value: expenseOrIncome.toUpperCase(),
    },
];

/**
 * Replaces filters in the filters array with those from extraFilters if they share the same field value.
 * If no overlapping fields are found, the two arrays are simply concatenated.
 */
export const replaceFiltersWithGlobalOnes = (
    filters: QueryFilterT[],
    extraFilters: QueryFilterT[],
): QueryFilterT[] => {
    const filterFields = filters.map((f) => f.field);
    const extraFiltersFields = extraFilters.map((f) => f.field);
    if (!_.isEmpty(_.intersection(filterFields, extraFiltersFields))) {
        const updatedFilters = filters.map(
            (f) => extraFilters.find((fltr) => fltr.field === f.field) || f,
        );
        const updatedFiltersFields = updatedFilters.map((f) => f.field);
        const updatedExtra = extraFilters.filter(
            (f) => !updatedFiltersFields.includes(f.field),
        );
        return [...updatedFilters, ...updatedExtra];
    }
    return [...filters, ...extraFilters];
};

/**
 * Filters out any filter that have a null, undefined, or falsy value.
 */
export const filterOutNullFilterValue = (
    filters: QueryFilterT[],
): QueryFilterT[] => filters.filter((fltr) => Boolean(fltr.value));

const getScopeNameForFilterField = (
    field: string,
    userScopes: UserScopesT,
): string => {
    const restricted = userScopes.filter(
        (uScope) => uScope.accessType === 'restricted',
    );
    const notEmpty = restricted.filter(
        (uScope) => !_.isEmpty(getBlockValues(field, uScope.blocks)),
    );
    return notEmpty.map((uScope) => `scope:${uScope.name}`)[0];
};

/**
 * TODO: Teach this function good manners. It's a liar.
 * Have you already met a liar? Because addScopeToFilter lies. A lot. Don't trust
 * this function, it won't work if requestBy's camelCase and snake_case are not equal, eg. org_chart_finance.tree
 * @param requestBy a legit resource field name
 * @param userScopes legit user scopes
 * @param scopes legit scopes
 * @returns an ugly lie, you liar. LIAR.
 */
export const addScopeToFilter = (
    requestBy: BudgetBys = DEFAULT_BUDGET_BY,
    userScopes: UserScopesT,
    scopes: ScopeT[],
): QueryFilterT[] => {
    if (hasFilterScope(snakify(requestBy), userScopes, scopes)) {
        return [
            {
                field:
                    requestBy === BudgetBys.GESTIONNAIRE
                        ? GESTIONNAIRE_SOFTWARE_ID
                        : requestBy,
                operation: FO.in_,
                value: getScopeNameForFilterField(
                    snakify(requestBy),
                    userScopes,
                ),
            },
        ];
    }
    return [];
};

const getAllFilters = (
    baseQueryElements: QueryT,
    querySettings: QuerySettingsT,
    userScopes: UserScopesT,
    scopes: ScopeT[],
    exercice: number,
    budgetSection?: BudgetSections | null,
): QueryFilterT[] => {
    const allFilters = filterOutNullFilterValue([
        ...baseQueryElements.filters,
        ...getFiltersFromBudgetProposals(
            querySettings.budgetProposals,
            budgetSection,
        ),
        ...getExerciceFilter(
            exercice,
            querySettings.hasExerciceRange,
            querySettings.isCadrage,
            querySettings.exerciceRange,
            querySettings.cadrageConfig,
        ),
        // FIXME: we should only use the resource software id
        ...getInFiltersForValues(querySettings.requestBy, [
            querySettings.requestBy === BudgetBys.GESTIONNAIRE
                ? querySettings.selectedOrgChart!
                : querySettings.resourceSoftwareId!,
        ]),
        ...(querySettings.expenseOrIncome !== null
            ? getExpenseOrIncomeFilter(querySettings.expenseOrIncome)
            : []),
        ...addScopeToFilter(querySettings.requestBy, userScopes, scopes),
    ]);
    return replaceFiltersWithGlobalOnes(allFilters, querySettings.extraFilters);
};

const getInscriptionsFilters = (
    baseQueryElements: QueryT,
    querySettings: QuerySettingsT,
    exercice: number,
    budgetSection?: BudgetSections | null,
    onlyOrphans: boolean = false,
): QueryFilterT[] => {
    const allFilters = filterOutNullFilterValue([
        ...baseQueryElements.filters,
        ...getInscriptionFiltersFromBudgetProposals(
            querySettings.budgetProposals,
            budgetSection,
        ),
        ...getInscriptionExerciceFilter(exercice),
        // FIXME: we should only use the resource software id
        ...getInFiltersForValues(querySettings.requestBy, [
            querySettings.requestBy === BudgetBys.GESTIONNAIRE
                ? querySettings.selectedOrgChart!
                : querySettings.resourceSoftwareId!,
        ]),
        ...(querySettings.expenseOrIncome !== null
            ? getInscriptionExpenseOrIncomeFilter(querySettings.expenseOrIncome)
            : []),
    ]);
    if (onlyOrphans) {
        allFilters.push({
            field: BUDGET_LINE_ID,
            operation: 'equal',
            value: null,
        });
    }
    return replaceFiltersWithGlobalOnes(allFilters, querySettings.extraFilters);
};

// Some fields are duplicated between inscription and budget_line,
// therefore for some query we need to replace the filter on budget_line to their equivalent field
// in inscription
export const replaceBudgetLineFilterToInscriptionFilter = (
    queryFilter: QueryFilterT,
): QueryFilterT => {
    if (queryFilter.field === SECTION_FIELD) {
        return { ...queryFilter, field: INSCRIPTION_INVESTMENT_OPERATING };
    }
    if (queryFilter.field === EXPENSE_INCOME_FIELD) {
        return { ...queryFilter, field: INSCRIPTION_EXPENSE_INCOME };
    }
    if (queryFilter.field === BUDGET_YEAR_FIELD) {
        return { ...queryFilter, field: INSCRIPTION_EXERCICE };
    }
    return queryFilter;
};

export const getRejectedInscriptionFiltersByPhase = (
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): QueryFilterT[] => {
    const statusField = getStatusFieldForPhase(phase);
    return [
        {
            // TODO StrictNullCheck : Try to remove "!"
            field: statusField!,
            operation: FO.not_in,
            value: getRejectedStatusesForPhase(phase, statusesSettings).join(
                '|',
            ),
        },
    ];
};

export const getRejectedInscriptionFilters = (
    currentPhase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): QueryFilterT[] =>
    [
        BudgetPhases.PROPOSITION_SERVICES,
        BudgetPhases.ARBITRAGE,
        BudgetPhases.SUPPLEMENTARY_BUDGET,
        BudgetPhases.ARBITRAGE_DM,
    ]
        .filter((p) => {
            if (
                !ORDERED_BUDGET_PHASES.includes(p) ||
                !ORDERED_BUDGET_PHASES.includes(currentPhase)
            ) {
                return false;
            }
            return (
                ORDERED_BUDGET_PHASES.indexOf(p) <=
                ORDERED_BUDGET_PHASES.indexOf(currentPhase)
            );
        })
        .map(
            (p) => getRejectedInscriptionFiltersByPhase(p, statusesSettings)[0],
        );

const getAllDimensions = (
    baseQueryElements: QueryT,
    querySettings: QuerySettingsT,
): QueryDimensionT[] => {
    const bps = querySettings.budgetProposals;
    const dims = baseQueryElements.dimensions;
    if (
        querySettings.addOperation &&
        bps.some((bp) => bp.section === BudgetSections.INVESTMENT)
    ) {
        return [
            ...dims,
            getDimField(SECONDARY_BUDGET_AXIS_ELEMENT_CODE, dims.length),
        ];
    }
    return dims;
};

export const getMetricsByBudgetPhase = (
    metrics: string[],
    budgetPhase: BudgetPhases,
): string[] => {
    if (isSimulatedPhase(budgetPhase)) {
        const fields = getPhasedFieldsUntilPhase(budgetPhase, 'amount');
        return [...metrics, ...fields];
    }

    // We do not want to see simulated phases here,
    // simulated Phases are actually a simulation of the next phase
    // so there is no point of seeing them once we moved to the next phase
    if (isActiveBudgetPhase(budgetPhase))
        return [
            ...metrics,
            ...getPhasedFieldsUntilPhase(
                budgetPhase,
                'amount',
                'isSimulated',
                false,
            ),
        ];
    return metrics;
};

/**
 * This function take a pre-defined query (baseQueryElements) and add some filters and dimensions
 * based on the settings given by querySettings.
 *
 * These settings are specific to the info in a given page.
 * For instance, in the budget proposal page, the pre-defined query needs to be filtered by
 * the budget name and the section provided in page.
 */
export const getBudgetQuery = (
    baseQueryElements: QueryT,
    querySettings: QuerySettingsT,
    userScopes: UserScopesT = [],
    scopes: ScopeT[],
    exercice: number,
    budgetSection?: BudgetSections | null,
): QueryT => {
    const queryElements = _.cloneDeep(baseQueryElements);
    queryElements.filters = getAllFilters(
        baseQueryElements,
        querySettings,
        userScopes,
        scopes,
        exercice,
        budgetSection,
    );
    queryElements.dimensions = getAllDimensions(
        baseQueryElements,
        querySettings,
    );
    return queryElements;
};

/**
 * This function does almost the same than 'getBudgetQuery'
 * but adapt the query filters to target only inscription related fields
 */
const getInscriptionBudgetQuery = (
    baseQueryElements: QueryT,
    querySettings: QuerySettingsT,
    exercice: number,
    budgetSection?: BudgetSections | null,
    onlyOrphans: boolean = false,
): QueryT => {
    const queryElements = _.cloneDeep(baseQueryElements);
    queryElements.filters = getInscriptionsFilters(
        baseQueryElements,
        querySettings,
        exercice,
        budgetSection,
        onlyOrphans,
    );
    queryElements.dimensions = getAllDimensions(
        baseQueryElements,
        querySettings,
    );
    return queryElements;
};

/**
Specific query to get the requested amount by <services>,
<services> being defined by the budget proposal
filtered by expense and by the budget name and section given by the budget proposal
*/
export const getBudgetVentilatedQuery = (
    budgetProposal: BudgetProposalT,
    ventilationBy: BudgetProposalVentilationBy,
    statusesSettings: StatusesSettingsT,
    extraFilters: QueryFilterT[] = [],
): QueryT => {
    const { by, phase } = budgetProposal;
    const rawQuery = getRawBudgetVentilatedQuery(
        by,
        ventilationBy,
        phase,
        statusesSettings,
    );

    const querySettings: QuerySettingsT = {
        budgetProposals: [budgetProposal],
        hasExerciceRange: false,
        requestBy: by,
        selectedOrgChart: null,
        resourceSoftwareId: null,

        expenseOrIncome: null,
        addOperation: false,

        extraFilters,
        isCadrage: false,

        orgChartFinanceTree: null,
        exerciceRange: 0,
    };
    return getInscriptionBudgetQuery(
        rawQuery,
        querySettings,
        budgetProposal.exercice,
        budgetProposal.section,
    );
};

/**
 * Replaces the `field` property in the filters array if it matches `GESTIONNAIRE_TABLE`,
 * setting it to `GESTIONNAIRE_SOFTWARE_ID`.
 */
export const replaceGestionnaireFieldInFilters = (filters: QueryFilterT[]) =>
    filters.map((filter) =>
        filter.field === GESTIONNAIRE_TABLE
            ? { ...filter, field: GESTIONNAIRE_SOFTWARE_ID }
            : filter,
    );

/**
Specific query for bar chart visualisation of all imputations
*/
export const getBarChartImputation = (
    rawQueryElements: QueryT,
    budgetProposal: BudgetProposalT,
    resourceSoftwareId: string,
    expenseOrIncome: BudgetExpenseOrIncome | null,
    userScopes: UserScopesT,
    scopes: ScopeT[],
    extraFilters: QueryFilterT[] = [],
    addOperation: boolean = false,
): QueryT => {
    const querySettings = {
        budgetProposals: [budgetProposal],
        hasExerciceRange: true,
        requestBy: budgetProposal.by,
        selectedOrgChart: null,
        resourceSoftwareId,
        expenseOrIncome,
        addOperation,
        isCadrage: false,
        extraFilters: replaceGestionnaireFieldInFilters(extraFilters),
        orgChartFinanceTree: null,
        exerciceRange: 3,
    };

    return getBudgetQuery(
        rawQueryElements,
        querySettings,
        userScopes,
        scopes,
        budgetProposal.exercice,
        budgetProposal.section,
    );
};

type GetProjectPivotGrid = {
    rawQueryElements: QueryT;
    budgetProposal: BudgetProposalT;
    resourceSoftwareId: string;
    expenseOrIncome?: BudgetExpenseOrIncome | null;
    extraFilters?: QueryFilterT[];
    addOperation?: boolean;
};

export const getProjectPivotGridDataQuery = ({
    rawQueryElements,
    budgetProposal,
    resourceSoftwareId,
    expenseOrIncome = null,
    extraFilters = [],
    addOperation = false,
}: GetProjectPivotGrid): QueryT => {
    const inscription_filters = extraFilters.map(
        replaceBudgetLineFilterToInscriptionFilter,
    );
    const querySettings = {
        budgetProposals: [budgetProposal],
        hasExerciceRange: false,
        requestBy: budgetProposal.by,
        selectedOrgChart: null,
        resourceSoftwareId,
        expenseOrIncome,
        addOperation,
        isCadrage: false,
        extraFilters: replaceGestionnaireFieldInFilters(inscription_filters),
        orgChartFinanceTree: null,
        exerciceRange: 0,
    };
    return getInscriptionBudgetQuery(
        rawQueryElements,
        querySettings,
        budgetProposal.exercice,
        budgetProposal.section,
    );
};
